import {useEffect, useState, createRef} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {applicationSelector, updateDesign} from '../../slices/application';
import {extractCanvasMeasurementsFromDesign} from '../../utils/utils';
import {useShadowRoot} from '../../contexts/ShadowRootContext';
import CanvasFooter from './CanvasFooter';
import CanvasHeader from './CanvasHeader';
import DrawLayer from './DrawLayer';
import SelectTubeLayer from './SelectTubeLayer';
import './SelectTubeLayer.css';
import TubeLayerCanvas from './TubeLayerCanvas';
import TubeMinimap from './TubeMinimap';

function useWindowSize() {
  // Initialize state with undefined width/height so server and client renders match
  // Learn more here: https://joshwcomeau.com/react/the-perils-of-rehydration/
  const [windowSize, setWindowSize] = useState({
    width: undefined,
    height: undefined
  });
  useEffect(() => {
    // Handler to call on window resize
    function handleResize() {
      // Set window width/height to state
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight
      });
    }

    // Add event listener
    window.addEventListener('resize', handleResize);
    // Call handler right away so state gets updated with initial window size
    handleResize();
    // Remove event listener on cleanup
    return () => window.removeEventListener('resize', handleResize);
  }, []); // Empty array ensures that effect is only run on mount
  return windowSize;
}

const Canvas = () => {
  const ref = createRef(null);
  const dispatch = useDispatch();
  const shadowRoot = useShadowRoot();

  const size = useWindowSize();

  function updateDesignWithCanvasSize() {
    let drawing = shadowRoot.getElementsByClassName?.('drawing')?.[0] || shadowRoot.querySelector?.('.drawing');

    // If the class name 'drawing' is not found, do not proceed with the function
    if (!drawing) {
      return;
    }
    // Add a counter to prevent infinite loop
    let counter = 0;
    while (
      (drawing?.clientHeight === 0 || drawing?.clientHeight == null) &&
      appTab == 'select-options' &&
      counter < 100
    ) {
      drawing = shadowRoot.getElementsByClassName?.('drawing')?.[0] || shadowRoot.querySelector?.('.drawing');
      counter++;
    }

    // Width comes from the wrapper (the stable viewport); the drawing layer itself
    // grows wider than the viewport when the tube overflows for horizontal scroll.
    const wrapper =
      shadowRoot.getElementsByClassName?.('hs-canvas-wrapper')?.[0] ||
      shadowRoot.querySelector?.('.hs-canvas-wrapper');
    const viewportWidth = wrapper?.clientWidth ?? drawing.clientWidth;

    // Check if the canvas has a non-zero width and height
    if (drawing?.clientHeight > 0 && viewportWidth > 0) {
      dispatch(
        updateDesign({
          canvasHeight: drawing.clientHeight,
          canvasWidth: viewportWidth
        })
      );
    } else {
      console.error('Cannot update design with a canvas of zero width or height');
    }
  }

  useEffect(() => {
    // Check if size has non-zero width and height
    if (size.width > 0 && size.height > 0) {
      updateDesignWithCanvasSize();
    }
  }, [size]);

  const {appTab, design} = useSelector(applicationSelector);
  // Slender tubes overflow the viewport and scroll horizontally; .hs-canvas becomes
  // the scroll container in that case. isOverflow is derived from the tube's fit
  // against the current viewport dimensions (see OVERFLOW_TUBE_HEIGHT_FRACTION),
  // so it may change when the canvas size changes (e.g. on resize).
  const isDesignCanvas =
    appTab === 'choose-print' || appTab === 'select-art' || appTab === 'choose-quantity';
  const isOverflow = isDesignCanvas && extractCanvasMeasurementsFromDesign(design).isOverflow;
  return (
    // Non-scrolling frame: takes .hs-canvas's flex slot so its box is unchanged,
    // and gives the minimap a stable positioned ancestor OUTSIDE the scroll
    // container, so it stays put during scroll without a per-scroll transform.
    <div className="hs-canvas-frame">
      <div className={`hs-canvas${isOverflow ? ' is-overflow' : ''}`}>
        <CanvasHeader />
        <div className="hs-canvas-wrapper" ref={ref}>
          {appTab === 'choose-tubing' ? <SelectTubeLayer /> : null}
          {appTab === 'choose-print' || appTab === 'select-art' || appTab === 'choose-quantity' ? (
            <>
              <TubeLayerCanvas />
              <DrawLayer />
            </>
          ) : null}
        </div>
        <CanvasFooter />
      </div>
      {isOverflow ? <TubeMinimap /> : null}
    </div>
  );
};

export default Canvas;
